import React, { useState, useEffect, useMemo } from 'react'
import {
  Filter, SortOrderAsc, SortOrder, SortOrderDesc, getTeamMamoriosWithoutUpdatingState,
} from '../../../../modules/Mamorio'
import TabMenu from '../../../navigation/TabMenu'
import { useAppDispatch, useAppSelector } from '../../../../hooks'
import MamorioList from '../MamorioList'
import './SettingsEditMamorio.css'
import SearchBar from '../../../overview/SearchBar'
import SettingTabsInfo from '../../../../constants/SettingTabsInfo'
import ReloadButton from '../../../overview/ReloadButton'
import ViewAllMamoriosInMapButton from '../../../overview/ViewAllMamoriosInMapButton'
import { showModal } from '../../../../modules/UI'
import { BASE_MODAL } from '../../../common/RootModal'
import GetRentalsModal from '../../modal/GetRentalsModal'
import MamoriosMapModal from '../../../overview/MamoriosMapModal'
import { getMamoriosWithLocation } from '../../../../helpers/CommonMamorioHelper'
import MamorioSettingList from '../MamorioSettingList'
import HeaderType from '../../../../modules/common/headerType'
import { showMapOnListTop } from '../../../../modules/common/session'
import CSVModal from '../../modal/CSVModal'
import { getErrorString } from '../../../../utils/errorUtils'
import ChannelService from '../../../../modules/ChannelService'
import { LastEventLogSortKey } from '../../../../helpers/lastEventLogHelper'

type SortAndOrder = {
  sort?: string
  order?: SortOrder
}

const SortKey = {
  Name: 'name',
  Memo: 'memo',
  Time: undefined,
  LastEventLogName: LastEventLogSortKey,
  BatteryLifeRessetedAt: 'battery_life_resetted_at',
  Status: 'status',
} as TSortKey

type Props = {
  showTabs?: Boolean
  showEditButton?: Boolean
}

const SettingsEditMamorio = ({ showTabs = true, showEditButton = true }: Props) => {
  const dispatch = useAppDispatch()
  const currentTeamDomain = useMemo(
    () => (process.env.REACT_APP_HOSTNAME || window.location.hostname)?.split('.')[0] || '',
    [],
  )
  const [filter, setFilter] = useState({
    teamDomain: currentTeamDomain,
    sort: 'name',
    order: SortOrderAsc as SortOrder,
    page: 1,
  })
  const team = useAppSelector((state) => state.team)
  const rentalGroup = team.groups?.find((group) => group.mode === 'rental')
  const rentalMode = Boolean(team.groups?.some((group) => group.mode === 'rental'))
  const hasMamorioCsvDownloadFeature = Boolean(team.mamorio_csv_download_enabled)
  const mamorios = useAppSelector((state) => state.mamorio.mamorios)
  const user = useAppSelector((state) => state.user)
  const memoEnabled = useMemo(
    () => Boolean(team.groups?.some((group) => group.memo_enabled)),
    [team.groups],
  )

  const showGetRentalsModal = (group: IGroup) => {
    dispatch(showModal({
      modalType: BASE_MODAL,
      modalProps: {
        title: '持ち出し履歴の出力',
        children: <GetRentalsModal group={group} closeModalFunction={() => {}} />,
      },
    }))
  }

  const reloadList = async () => {
    setFilter({ ...filter })
  }

  const showCSVModal = () => {
    dispatch(showModal({
      modalType: BASE_MODAL,
      modalProps: {
        title: 'CSVによる出力と更新',
        children: <CSVModal didUpdteMamorios={reloadList} closeModalFunction={() => {}} />,
      },
    }))
  }

  const showMamoriosMapModal = () => {
    if (!mamorios) return

    const showMap = (_mamorios: IMamorio[]) => {
      dispatch(showModal({
        modalType: MamoriosMapModal,
        modalProps: {
          mamorios: getMamoriosWithLocation(_mamorios),
        },
      }))
    }

    dispatch(getTeamMamoriosWithoutUpdatingState({
      filter: {
        teamDomain: currentTeamDomain,
        sort: 'name',
        order: SortOrderAsc as SortOrder,
        page: 1,
        maxMamorios: true,
      },
    })).unwrap().then((result) => {
      showMap(result.mamorios)
    }).catch((error) => {
      console.error(getErrorString(error))
      showMap(mamorios)
    })
  }

  const updateGroupFilter = async (newFilter: Filter) => {
    setFilter({ ...filter, ...newFilter, page: 1 })
  }

  const updatePageNumber = (pageNumber: number) => {
    setFilter({ ...filter, page: pageNumber })
  }

  const updateSort = (type: HeaderType) => {
    let params = {
      sort: '',
      order: SortOrderAsc,
    } as SortAndOrder
    const { sort, order } = filter
    switch (type) {
      case HeaderType.Name:
        if (sort === SortKey.Time) {
          params = {
            sort: SortKey.Name,
            order: SortOrderAsc,
          } as SortAndOrder
          break
        }

        params = {
          sort: SortKey.Name,
          order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
        }
        break
      case HeaderType.Memo:
        if (sort === SortKey.Time) {
          params = {
            sort: SortKey.Memo,
            order: SortOrderAsc,
          } as SortAndOrder
          break
        }

        params = {
          sort: SortKey.Memo,
          order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
        }
        break
      case HeaderType.Time:
        if (sort === SortKey.Name) {
          params = {
            sort: SortKey.Time,
            order: SortOrderAsc,
          } as SortAndOrder
          break
        }
        params = {
          sort: SortKey.Time,
          order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
        }
        break
      case HeaderType.LastEventLogName:
        if (sort !== SortKey.LastEventLogName) {
          params = {
            sort: SortKey.LastEventLogName,
            order: SortOrderAsc,
          } as SortAndOrder
          break
        }
        params = {
          sort: SortKey.LastEventLogName,
          order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
        }
        break
      case HeaderType.BatteryLifeRessetedAt:
        if (sort !== SortKey.BatteryLifeRessetedAt) {
          params = {
            sort: SortKey.BatteryLifeRessetedAt,
            order: SortOrderAsc,
          } as SortAndOrder
          break
        }
        params = {
          sort: SortKey.BatteryLifeRessetedAt,
          order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
        }
        break
      case HeaderType.Status:
        if (sort !== SortKey.Status) {
          params = {
            sort: SortKey.Status,
            order: SortOrderAsc,
          } as SortAndOrder
          break
        }
        params = {
          sort: SortKey.Status,
          order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
        }
        break
      default:
        // eslint-disable-next-line no-console
        console.warn(`${type} does not exist type`)
        break
    }

    setFilter({ ...filter, ...params, page: 1 })
  }

  const tabsInfo = showTabs
    ? SettingTabsInfo({
      groups: team.groups ?? null,
      deviceEnabled: team.device_enabled,
      qrTagEnabled: team.qr_tag_enabled ?? false,
      eventNotificationSettingsEnable: team.event_notification_settings_enable ?? false,
    })
    : []
  const listTitle = showTabs ? 'MAMORIOの設定' : '閲覧可能なMAMORIO一覧'

  useEffect(() => {
    if (typeof window.ChannelIO === 'undefined') {
      ChannelService.bootWithMember(user.user?.id)
    }
  }, [user.user?.id])

  return (
    <TabMenu tabsInfo={tabsInfo}>
      <div className="title">
        <div>
          {listTitle}
        </div>
        <div className="page-title">
          <div>
            <ReloadButton onClick={reloadList} />
          </div>
          <div>
            <ViewAllMamoriosInMapButton onClick={showMamoriosMapModal} />
          </div>
          {rentalGroup && (
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => showGetRentalsModal(rentalGroup)}
              >
                持ち出し履歴の出力
              </button>
            </div>
          )}
          {memoEnabled && (
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => showCSVModal()}
              >
                CSV
              </button>
            </div>
          )}
        </div>
      </div>
      <SearchBar
        rentalMode={rentalMode}
        loadList={() => {}}
        updateGroupFilter={updateGroupFilter}
        showDeviceSwitch={false}
        memoEnabled={memoEnabled}
      />
      {(showEditButton!) ? (
        <MamorioSettingList
          filter={filter}
          onChangeSort={updateSort}
          onClickPagination={updatePageNumber}
          csvDownloadEnabled={hasMamorioCsvDownloadFeature}
          rentalMode={rentalMode}
        />
      ) : (
        <MamorioList
          filter={filter}
          onChangeSort={updateSort}
          onClickPagination={updatePageNumber}
          showMap={showMapOnListTop()}
          csvDownloadEnabled={hasMamorioCsvDownloadFeature}
          rentalMode={rentalMode}
        />
      )}
    </TabMenu>
  )
}

SettingsEditMamorio.renderInfo = () => (
  <p className="message">
    MAMORIOを追加したい際は
    <a target="blank" href="https://mamorio.typeform.com/to/dwak6v">
      MAMORIOまでお問い合わせ
    </a>
    ください。
  </p>
)

export default SettingsEditMamorio
